/**
 * Exact rational arithmetic for the quantity engine.
 *
 * Every quantity of handover rev7 §6.7 is a ratio of small whole numbers —
 * guests × pieces per guest × weight ÷ total weight × margin — and the rule
 * that turns one into a production unit is "round up". In binary floating
 * point a quantity that is exactly 40 comes out as 40.000000000000007, and
 * rounding that up adds a whole production unit nobody asked for. The
 * customer page works around that with a tolerance; a server that writes
 * `order_lines` and an invoice should not need one, so the engine works in
 * BigInt fractions and never sees the artefact.
 *
 * Money is a different matter: forint amounts are whole numbers by the time
 * §5.11 rounds VAT, so they stay `number`.
 */

export interface Rational {
  readonly n: bigint
  readonly d: bigint
}

const gcd = (a: bigint, b: bigint): bigint => {
  let x = a < 0n ? -a : a
  let y = b < 0n ? -b : b
  while (y) {
    const t = x % y
    x = y
    y = t
  }
  return x
}

/** A fraction in lowest terms with a positive denominator. */
export function rational(n: bigint | number, d: bigint | number = 1n): Rational {
  let num = BigInt(n)
  let den = BigInt(d)
  if (den === 0n) throw new Error('rational: division by zero')
  if (den < 0n) {
    num = -num
    den = -den
  }
  const k = gcd(num, den) || 1n
  return { n: num / k, d: den / k }
}

export const ZERO: Rational = rational(0n)
export const ONE: Rational = rational(1n)

/**
 * A decimal literal read exactly: `fromDecimal('1.15')` is 23/20, not the
 * nearest double. Accepts a comma as well as a point, because the register
 * and the handover write 8,5 and the seed writes 8.5.
 */
export function fromDecimal(value: string | number): Rational {
  const str = String(value).trim().replace(',', '.')
  if (!/^-?\d*(\.\d*)?$/.test(str) || str === '' || str === '-') {
    throw new Error(`fromDecimal: not a decimal literal: ${JSON.stringify(value)}`)
  }
  const negative = str.startsWith('-')
  const body = negative ? str.slice(1) : str
  const [whole = '', fraction = ''] = body.split('.')
  const digits = (whole || '0') + fraction
  const n = BigInt(digits === '' ? '0' : digits) * (negative ? -1n : 1n)
  return rational(n, 10n ** BigInt(fraction.length))
}

export const add = (a: Rational, b: Rational): Rational => rational(a.n * b.d + b.n * a.d, a.d * b.d)
export const subtract = (a: Rational, b: Rational): Rational => rational(a.n * b.d - b.n * a.d, a.d * b.d)
export const multiply = (a: Rational, b: Rational): Rational => rational(a.n * b.n, a.d * b.d)
export const divide = (a: Rational, b: Rational): Rational => {
  if (b.n === 0n) throw new Error('divide: division by zero')
  return rational(a.n * b.d, a.d * b.n)
}

/** −1, 0 or 1. */
export const compare = (a: Rational, b: Rational): number => {
  const left = a.n * b.d
  const right = b.n * a.d
  return left < right ? -1 : left > right ? 1 : 0
}

export const isZero = (a: Rational): boolean => a.n === 0n
export const isPositive = (a: Rational): boolean => a.n > 0n
export const max = (a: Rational, b: Rational): Rational => (compare(a, b) >= 0 ? a : b)
export const min = (a: Rational, b: Rational): Rational => (compare(a, b) <= 0 ? a : b)

/** For display and for comparisons that have already been decided exactly. */
export const toNumber = (a: Rational): number => Number(a.n) / Number(a.d)

const floorBigInt = (a: Rational): bigint => {
  const q = a.n / a.d
  return a.n % a.d !== 0n && a.n < 0n ? q - 1n : q
}

const ceilBigInt = (a: Rational): bigint => {
  const q = a.n / a.d
  return a.n % a.d !== 0n && a.n > 0n ? q + 1n : q
}

export const floorToInteger = (a: Rational): number => Number(floorBigInt(a))
export const ceilToInteger = (a: Rational): number => Number(ceilBigInt(a))

/** Round half up to a whole number. §5.11 rounds VAT this way. */
export const roundHalfUpToInteger = (a: Rational): number =>
  Number(floorBigInt(add(a, rational(1n, 2n))))

/** §6.7 step 5: up to whole production units of `unit` pieces, never past them. */
export function ceilToUnits(a: Rational, unit: number): number {
  if (!Number.isInteger(unit) || unit <= 0) throw new Error(`ceilToUnits: bad production unit ${unit}`)
  return Number(ceilBigInt(divide(a, rational(unit)))) * unit
}

/** §6.7 step 6: down to whole production units, for the reduced share. */
export function floorToUnits(a: Rational, unit: number): number {
  if (!Number.isInteger(unit) || unit <= 0) throw new Error(`floorToUnits: bad production unit ${unit}`)
  return Number(floorBigInt(divide(a, rational(unit)))) * unit
}

/** A decimal string with `places` digits, rounded half up. §5.4 shows one. */
export function toFixed(a: Rational, places: number): string {
  const scale = rational(10n ** BigInt(places))
  const scaled = roundHalfUpToInteger(multiply(a, scale))
  const negative = scaled < 0
  const digits = String(Math.abs(scaled)).padStart(places + 1, '0')
  const whole = digits.slice(0, digits.length - places)
  const fraction = places > 0 ? '.' + digits.slice(digits.length - places) : ''
  return (negative ? '-' : '') + whole + fraction
}
